/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useEffect, useState } from "react";

type Props = {
  url: string;
  className?: string;
  svgAriaHidden?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const svgMarkupCache = new Map<string, string>();
const svgMarkupPromises = new Map<string, Promise<string>>();

const loadSvgMarkup = (url: string): Promise<string> => {
  if (svgMarkupCache.has(url)) {
    return Promise.resolve(svgMarkupCache.get(url) ?? "");
  }

  const cachedPromise = svgMarkupPromises.get(url);

  if (cachedPromise) {
    return cachedPromise;
  }

  const promise = fetch(url)
    .then((res) => res.text())
    .then((markup) => {
      svgMarkupCache.set(url, markup);

      return markup;
    })
    .catch((error) => {
      svgMarkupPromises.delete(url);

      throw error;
    });

  svgMarkupPromises.set(url, promise);

  return promise;
};

const hideInlineSvg = (markup: string) => {
  const document = new DOMParser().parseFromString(markup, "image/svg+xml");
  const svg = document.documentElement;

  if (svg.tagName.toLowerCase() !== "svg") {
    return markup;
  }

  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  return new XMLSerializer().serializeToString(svg);
};

export const SvgInline = ({ svgAriaHidden, url, ...rest }: Props) => {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    loadSvgMarkup(url)
      .then((markup) => {
        if (isMounted) {
          setSvg(svgAriaHidden ? hideInlineSvg(markup) : markup);
        }
      })
      .catch(() => {
        if (isMounted) {
          setSvg(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [svgAriaHidden, url]);

  if (!svg) {
    return null;
  }

  return <div {...rest} dangerouslySetInnerHTML={{ __html: svg }} />;
};
